import firebaseConfig from '../firebase-applet-config.json';
import { initializeApp } from 'firebase/app';
import { initializeFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const app = initializeApp(firebaseConfig);

// Initialize Firestore with custom database ID from config as per official Firebase skill
export const db = initializeFirestore(app, { experimentalForceLongPolling: true }, firebaseConfig.firestoreDatabaseId);
export const auth = getAuth(app);

// Global interceptor for unhandled Firestore SDK offline / stream notices
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  console.error = (...args: any[]) => {
    const text = args.map(a => (typeof a === 'string' ? a : a?.message || '')).join(' ');
    if (text.includes('Could not reach Cloud Firestore backend') || text.includes('backend didn\'t respond within 10 seconds')) {
      // Benign SDK offline notice; log as low-priority debug notice
      console.debug('Firestore connection info (offline mode active):', text);
      return;
    }
    originalConsoleError.apply(console, args);
  };

  console.warn = (...args: any[]) => {
    const text = args.map(a => (typeof a === 'string' ? a : a?.message || '')).join(' ');
    if (text.includes('Could not reach Cloud Firestore backend') || text.includes('backend didn\'t respond within 10 seconds')) {
      console.debug('Firestore connection info (offline mode active):', text);
      return;
    }
    originalConsoleWarn.apply(console, args);
  };

  window.addEventListener('error', (event) => {
    const msg = event.message || '';
    if (msg.includes('FIRESTORE') || msg.includes('Could not reach Cloud Firestore backend')) {
      console.debug('Handled Firestore connection status:', msg);
      event.preventDefault();
      event.stopPropagation();
    }
  });
  
  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason?.message || String(event.reason);
    if (msg.includes('FIRESTORE') || msg.includes('Could not reach Cloud Firestore backend')) {
      console.debug('Handled Firestore connection status rejection:', msg);
      event.preventDefault();
      event.stopPropagation();
    }
  });
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const isQuota = errorMessage.toLowerCase().includes('quota') || errorMessage.toLowerCase().includes('limit');
  const isOffline = errorMessage.toLowerCase().includes('offline') || errorMessage.toLowerCase().includes('internet') || errorMessage.toLowerCase().includes('unavailable') || errorMessage.toLowerCase().includes('could not reach');
  const isTooLarge = errorMessage.toLowerCase().includes('exceeds the maximum allowed size') || errorMessage.toLowerCase().includes('size too large');
  const isBenign = errorMessage.toLowerCase().includes('cancelled') || errorMessage.toLowerCase().includes('idle stream') || errorMessage.includes('INTERNAL ASSERTION FAILED');

  // Silently ignore benign background stream timeouts or offline notices during read/list operations
  if ((isBenign || isOffline) && (operationType === OperationType.LIST || operationType === OperationType.GET)) {
    return;
  }

  let friendlyMessage = "A technical issue occurred. Please try again.";
  if (isQuota) {
    friendlyMessage = "The service is currently experiencing high traffic. Please try again in a few minutes.";
  } else if (isOffline) {
    friendlyMessage = "Operating in offline mode. Changes will be synced once connection is restored.";
  } else if (isTooLarge) {
    friendlyMessage = "Order too large. Please try reducing the number of items or contact support.";
  }

  const errInfo: FirestoreErrorInfo = {
    error: errorMessage,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
    },
    operationType,
    path
  };
  
  if (operationType === OperationType.LIST || operationType === OperationType.GET || isQuota || isBenign || isOffline) {
    console.warn('Firestore Operation Status: ', JSON.stringify(errInfo));
    return;
  }
  
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(friendlyMessage);
}

